'use client';

import { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import type { Options } from '@splidejs/splide';
import '@splidejs/react-splide/css';
import styles from './videogroup.module.scss';
import { ChevronIcon } from '@/assets/icons/ChevronIcon';
import { responsiveBreakpointsPx } from '@/constants';
import { Video, VideoProps } from '../Video/Video';
import { Modal } from '../../Modal/Modal';
import { VideoPlayer } from '../Video/VideoPlayer';
import useViewportWidth from '@/hooks/useViewportWidth';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import useClickOutside from '@/hooks/useClickOutside';
import { useOnScreen } from '@/hooks';
// TODO: dynamically import this component once migrated into de-sxm-www
import { FocusTrap } from 'focus-trap-react';
import { Button } from '@/ui-components/atoms/Button/Button';

export interface VideoGroupProps {
    videoSlides: VideoProps[];
    sliderAriaLabel?: string;
    className?: string;
    inlineVideo?: React.ReactElement<typeof Video>;
}

export function VideoGroup({
    videoSlides,
    sliderAriaLabel,
    className,
    inlineVideo,
}: VideoGroupProps): React.JSX.Element {
    const MODAL_CLICK_THROUGH_EXCEPTIONS_SELECTOR = '#onetrust-consent-sdk';
    const SLIDE_TRANSITION_SPEED_MS = 300;

    const splideRef = useRef<Splide | null>(null);
    const ref = useRef<HTMLDivElement>(null);
    const generalSplideRef = useRef<Splide | null>(null);
    const [focusPause, setFocusPause] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showArrows, setShowArrows] = useState(true);
    const [modalSlideSpeed, setModalSlideSpeed] = useState(SLIDE_TRANSITION_SPEED_MS);
    const [syncTimes, setSyncTimes] = useState<Record<number, number>>({});
    const [currentVideoTimes, setCurrentVideoTimes] = useState<Record<number, number>>({});
    const sliderClassNames = cn(styles.slider, className);

    const prevArrowClassNames = cn('splide__arrow splide__arrow--prev', styles.prevArrow);
    const nextArrowClassNames = cn('splide__arrow splide__arrow--next', styles.nextArrow);

    const modalPrevArrowClassNames = cn('splide__arrow splide__arrow--prev', styles.modalPrevArrow);
    const modalNextArrowClassNames = cn('splide__arrow splide__arrow--next', styles.modalNextArrow);

    const sliderContainerRef = useRef<HTMLDivElement>(null);
    const viewportWidth = useViewportWidth();
    const isMd = useMediaQuery(`(min-width: ${responsiveBreakpointsPx.md}px)`);
    const isLg = useMediaQuery(`(min-width: ${responsiveBreakpointsPx.lg}px)`);
    const isLgWithMany = videoSlides.length > 4 && isLg;

    const { isOnScreen } = useOnScreen(sliderContainerRef);
    const [hasBeenOnScreen, setHasBeenOnScreen] = useState(false);

    if (isOnScreen && !hasBeenOnScreen) {
        setHasBeenOnScreen(true);
    }

    useEffect(() => {
        const getTrack = () =>
            sliderContainerRef.current?.querySelector(`.${styles.sliderTrack}`) as HTMLDivElement | null;

        const track = getTrack();

        if (!track) return;

        const observer = new ResizeObserver(() => {
            requestAnimationFrame(() => {
                const splide = generalSplideRef.current?.splide;

                if (!splide) return;

                splide.refresh();

                const list = track.querySelector('.splide__list');
                const isScrollable = (list?.scrollWidth ?? track.scrollWidth) > track.clientWidth;

                setShowArrows(isScrollable && (isMd || isLgWithMany));
            });
        });

        observer.observe(track);

        const slides = track.querySelectorAll('.splide__slide');

        slides.forEach((slide) => observer.observe(slide));

        return () => observer.disconnect();
    }, [isMd, isLgWithMany, hasBeenOnScreen]);
    const VideoGroupSliderOptions: Options = {
        rewind: false,
        focus: 0,
        omitEnd: true,
        autoWidth: true,
        arrows: showArrows,
        perMove: 1,
        gap: '16px',
        pagination: false,
        drag: true,
        speed: SLIDE_TRANSITION_SPEED_MS,
        easing: 'ease-out',
        breakpoints: {
            [responsiveBreakpointsPx.md]: {
                arrows: false,
                autoWidth: true,
            },
            [responsiveBreakpointsPx.sm]: {
                arrows: false,
                autoWidth: true,
            },
        },
        lazyLoad: true,
    };

    useEffect(() => {
        if (isModalOpen && splideRef.current) {
            const timeoutId = setTimeout(() => {
                setModalSlideSpeed(0);
                splideRef.current?.go(activeIndex);
                setModalSlideSpeed(SLIDE_TRANSITION_SPEED_MS);
            }, 0);

            return () => clearTimeout(timeoutId);
        }

        const timeoutId = setTimeout(() => {
            setModalSlideSpeed(0);
        }, 0);

        return () => clearTimeout(timeoutId);
    }, [isModalOpen, activeIndex]);

    useClickOutside(ref, {}, (state, event) => {
        if (event) {
            if (event.target) {
                const target = event.target as HTMLElement;
                const closest = target.closest(MODAL_CLICK_THROUGH_EXCEPTIONS_SELECTOR);

                setFocusPause(!!closest);
            }
        }
    });

    const handleButtonClick = (index: number) => {
        setActiveIndex(index);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        const nextSyncTime = activeIndex >= 0 ? (currentVideoTimes[activeIndex] ?? syncTimes[activeIndex] ?? 0) : 0;
        if (activeIndex >= 0) {
            setSyncTimes((prev) => ({ ...prev, [activeIndex]: nextSyncTime }));
        }
        setIsModalOpen(false);
        setActiveIndex(-1);
    };

    const handleVideoTimeSync = (index: number, time: number) => {
        setCurrentVideoTimes((prev) => {
            if (prev[index] === time) {
                return prev;
            }

            return { ...prev, [index]: time };
        });
    };
    const handleFocus = (index: number) => {
        const splide = generalSplideRef?.current;
        if (splide) {
            setTimeout(() => {
                splide?.go(index);
            }, 150);
        }
    };

    return (
        <>
            {inlineVideo ? (
                <div className={styles.videoContainer}>{inlineVideo}</div>
            ) : (
                <div>
                    {isModalOpen && (
                        <FocusTrap
                            active={isModalOpen}
                            paused={focusPause}
                            focusTrapOptions={{
                                allowOutsideClick: true,
                            }}
                        >
                            <Modal
                                variant="video"
                                maxWidth={viewportWidth < 1188 ? viewportWidth : 1188}
                                backgroundTransparent={true}
                                open={isModalOpen}
                                onClose={handleCloseModal}
                                dataLocalpagename="overlay:videoslide"
                            >
                                <Splide
                                    className={styles.modalSlider}
                                    ref={splideRef}
                                    key={viewportWidth}
                                    hasTrack={false}
                                    options={{
                                        start: activeIndex,
                                        rewind: false,
                                        focus: 'center',
                                        arrows: videoSlides?.length > 1,
                                        perPage: 1,
                                        perMove: 1,
                                        pagination: false,
                                        drag: false,
                                        speed: modalSlideSpeed,
                                        easing: 'ease-out',
                                        lazyLoad: true,
                                    }}
                                    onMove={(splide, newIndex) => setActiveIndex(newIndex)}
                                >
                                    <SplideTrack>
                                        {videoSlides.map((slide, i) => (
                                            <SplideSlide key={i} className={styles.splideSlideItem}>
                                                {i === activeIndex ? (
                                                    <VideoPlayer
                                                        className={styles.videoModal}
                                                        {...slide}
                                                        initiatePlayer={true}
                                                        activeVideo={true}
                                                        resumeFromLastTime
                                                        isModalVideo={true}
                                                        onTimeSync={(time) => handleVideoTimeSync(i, time)}
                                                        syncTime={syncTimes[i]}
                                                    />
                                                ) : (
                                                    <div className={styles.videoModal} />
                                                )}
                                            </SplideSlide>
                                        ))}
                                    </SplideTrack>
                                    <div className={`splide__arrows`}>
                                        <Button
                                            variant="primary"
                                            iconButton={true}
                                            size={'large'}
                                            type={'button'}
                                            colorSet={'overlay'}
                                            className={modalPrevArrowClassNames}
                                        >
                                            <ChevronIcon />
                                        </Button>

                                        <Button
                                            variant="primary"
                                            iconButton={true}
                                            size={'large'}
                                            type={'button'}
                                            colorSet={'overlay'}
                                            className={modalNextArrowClassNames}
                                        >
                                            <ChevronIcon />
                                        </Button>
                                    </div>
                                </Splide>
                            </Modal>
                        </FocusTrap>
                    )}
                    <div ref={sliderContainerRef}>
                        {hasBeenOnScreen ? (
                            <Splide
                                className={sliderClassNames}
                                hasTrack={false}
                                options={VideoGroupSliderOptions}
                                aria-label={sliderAriaLabel}
                                ref={generalSplideRef}
                                aria-live="off"
                            >
                                <SplideTrack className={styles.sliderTrack} aria-live="off">
                                    {videoSlides?.map((slide, i) => {
                                        return (
                                            <SplideSlide key={i} aria-live="off">
                                                <Video
                                                    {...slide}
                                                    isVideoGroup
                                                    handlePlayClick={() => handleButtonClick(i)}
                                                    onFocus={() => handleFocus(i)}
                                                />
                                            </SplideSlide>
                                        );
                                    })}
                                </SplideTrack>
                                <div className={`splide__arrows ${styles.carouselArrows}`}>
                                    <button className={prevArrowClassNames}>
                                        <ChevronIcon />
                                    </button>
                                    <button className={nextArrowClassNames}>
                                        <ChevronIcon />
                                    </button>
                                </div>
                            </Splide>
                        ) : (
                            <div className={cn('splide', sliderClassNames)} aria-label={sliderAriaLabel}>
                                <div className={cn('splide__track', styles.sliderTrack)}>
                                    <div className="splide__list">
                                        {videoSlides?.map((slide, i) => (
                                            <div key={i} className="splide__slide">
                                                <Video
                                                    {...slide}
                                                    isVideoGroup
                                                    handlePlayClick={() => handleButtonClick(i)}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
